import {
  Button,
  Checkbox,
  Field,
  FluentProvider,
  Input,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  MenuItem,
  MenuList,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Switch,
  TagGroup,
  TagGroupProps,
  Textarea,
  Tree,
  TreeItem,
  TreeItemLayout,
  webLightTheme,
} from '@fluentui/react-components';
import { ArrowLeftRegular, FilterRegular, SearchRegular } from '@fluentui/react-icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IPubCenterMetadataElement } from '../../DashboardCards/@data/store/schema';
import { filterKeyToDisplayName, filterNameToPayloadKeyFunc } from '../utils/utils';
import { useFilterComponentStyles } from './FilterComponent.styles';

interface IFilterComponentsProps {
  filters: {};
  onSelectedFilterChange: React.Dispatch<
    React.SetStateAction<{
      [key: string]: string[];
    }>
  >; // Optional callback to handle selected filters
  existingFilters: { [key: string]: string[] }; // Existing filters to be displayed
  reportType?: string;
  disabled?: boolean;
}

type FilterOptions = {
  [key: string]: IPubCenterMetadataElement[];
};

export const FilterComponent: React.FC<IFilterComponentsProps> = ({
  filters,
  existingFilters,
  onSelectedFilterChange,
  reportType,
  disabled,
}) => {
  const [subOptionsVisible, setSubOptionsVisible] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSelected, setShowSelected] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const styles = useFilterComponentStyles();
  const allOptions: FilterOptions = filters;

  const handleFilterClick = (filter: string) => {
    setCurrentFilter(filter);
    setSubOptionsVisible(true);
    setMenuOpen(true);

    setSelectedItems(existingFilters[filterNameToPayloadKeyFunc(reportType)[filter]] || []);
  };

  const handleSubOptionToggle = (id: string) => {
    setSelectedItems((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  // Clears all selected items
  const clearAll = () => {
    setSelectedItems([]);
  };

  const applyFilter = () => {
    if (currentFilter) {
      onSelectedFilterChange((prev) => ({
        ...prev,
        [filterNameToPayloadKeyFunc(reportType)[currentFilter]]: selectedItems,
      }));
    }

    setSubOptionsVisible(false);
    setMenuOpen(false);
  };

  const cancelFilter = () => {
    setSubOptionsVisible(false);
    setMenuOpen(false);
  };

  const clearFilter = (filterName: string) => {
    onSelectedFilterChange((prev) => {
      const updatedFilters = { ...prev };
      delete updatedFilters[filterNameToPayloadKeyFunc(reportType)[filterName]];
      return updatedFilters;
    });
  };

  const removeItem: TagGroupProps['onDismiss'] = (_e, { value }) => {
    clearFilter(value as string);
  };

  const getFilteredOptions = (
    options: IPubCenterMetadataElement[],
    query: string,
    showSelectedFilter: boolean,
    selectedFilterItems: string[]
  ): IPubCenterMetadataElement[] => {
    if (!options || options.length === 0) {
      return [];
    }

    return options
      .map((option) => {
        // Recursively filter children if present
        let filteredChildren: IPubCenterMetadataElement[] | undefined = undefined;
        if (option.children && option.children.length > 0) {
          filteredChildren = getFilteredOptions(option.children, query, showSelectedFilter, selectedFilterItems);
        }

        // Check if this option matches the search or is selected
        const matchesQuery =
          option.name.toLowerCase().includes(query.toLowerCase()) || option.id.toLowerCase().includes(query.toLowerCase());

        const isSelected = selectedFilterItems.includes(option.id);

        // Show if:
        // - matches query
        // - or has filtered children
        // - or showSelectedFilter is on and is selected
        const shouldShow = (showSelectedFilter && isSelected) || matchesQuery || (filteredChildren && filteredChildren.length > 0);

        if (!shouldShow) {
          return null;
        }

        return {
          ...option,
          children: filteredChildren,
        };
      })
      .filter(Boolean) as IPubCenterMetadataElement[];
  };

  // Memoized filtered options
  const filteredOptions = useMemo(
    () => (currentFilter ? getFilteredOptions(allOptions[currentFilter], searchQuery, showSelected, selectedItems) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentFilter, searchQuery, showSelected, selectedItems, allOptions]
  );

  const menuListRef = useRef<HTMLDivElement | null>(null);
  const subOptionsListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (menuOpen) {
      const target = subOptionsVisible ? subOptionsListRef.current : menuListRef.current;
      target?.focus();
    }
  }, [menuOpen, subOptionsVisible]);

  // Renders tree items recursively
  const renderTreeItems = (options: IPubCenterMetadataElement[], currentSelectedFilter: string | null, paddingLeft: number = 10) => {
    const isFreeTextFilter =
      currentSelectedFilter &&
      (currentSelectedFilter.toLowerCase() === 'partner tag' ||
        currentSelectedFilter.toLowerCase() === 'form code' ||
        currentSelectedFilter.toLowerCase() === 'type tag' ||
        currentSelectedFilter.toLowerCase() === 'experiences' ||
        currentSelectedFilter.toLowerCase() === 'countries' ||
        currentSelectedFilter.toLowerCase() === 'languages' ||
        currentSelectedFilter.toLowerCase() === 'clarity project id' ||
        currentSelectedFilter.toLowerCase() === 'content experiences');

    if (!options || (options.length === 0 && !isFreeTextFilter)) {
      return null;
    }
    const leafPaddingLeft = 45; // Adjust this value to set the indentation level for nested items

    if (currentSelectedFilter && currentSelectedFilter.toLowerCase() === 'addistribution') {
      return (
        <RadioGroup onChange={(_, data) => setSelectedItems([data.value])}>
          {options.map((option) => (
            <TreeItem key={option.id} itemType="leaf">
              <TreeItemLayout>
                <Radio key={option.id} value={option.id} label={option.name} checked={selectedItems.includes(option.id)} />
              </TreeItemLayout>
            </TreeItem>
          ))}
        </RadioGroup>
      );
    } else if (currentSelectedFilter && isFreeTextFilter) {
      return (
        <Field label={`Enter ${currentSelectedFilter}`}>
          <Textarea
            placeholder="Enter one search term per line"
            value={selectedItems.join('\n')}
            onChange={(e) => setSelectedItems(e.target.value.split('\n').map((item) => item.replace(/\s+/g, ' ').trim()))}
            style={{ width: '100%' }}
          />
        </Field>
      );
    }

    return options.map((option) => {
      if (option.children && option.children.length > 0) {
        // Branch node
        return (
          <TreeItem key={option.id} itemType="branch">
            <TreeItemLayout>
              <Checkbox
                label={option.name}
                checked={option.children.every((child) => selectedItems.includes(child.id))}
                onChange={() =>
                  option.children!.every((child) => selectedItems.includes(child.id))
                    ? setSelectedItems((prev) => prev.filter((id) => !option.children!.some((child) => child.id === id)))
                    : setSelectedItems((prev) => [
                        ...prev,
                        ...option.children!.filter((child) => !prev.includes(child.id)).map((child) => child.id),
                      ])
                }
                className={styles.checkbox}
              />
            </TreeItemLayout>
            <Tree>{renderTreeItems(option.children, currentSelectedFilter, leafPaddingLeft)}</Tree>
          </TreeItem>
        );
      } else {
        // Leaf node
        return (
          <TreeItem key={option.id} itemType="leaf" style={{ cursor: 'default' }}>
            <TreeItemLayout style={{ paddingLeft: `${paddingLeft}px` }}>
              <Checkbox
                label={option.name}
                checked={selectedItems.includes(option.id)}
                onChange={() => handleSubOptionToggle(option.id)}
                className={styles.checkbox}
              />
            </TreeItemLayout>
          </TreeItem>
        );
      }
    });
  };

  const FiltersOptions = () => {
    return (
      <InteractionTag size="medium" shape="circular" disabled={disabled}>
        <Popover positioning="below-start" trapFocus open={menuOpen} onOpenChange={(e, data) => setMenuOpen(data.open)}>
          <PopoverTrigger>
            <InteractionTagPrimary icon={<FilterRegular style={{ marginRight: '5px' }} />} className={styles.filterButtonStyle}>
              Filter
            </InteractionTagPrimary>
          </PopoverTrigger>
          <PopoverSurface className={styles.filterOptionsDropdownStyle}>
            {!subOptionsVisible ? (
              <MenuList ref={menuListRef}>
                {Object.entries(allOptions).map(([filterName, items]) => {
                  return (
                    <MenuItem key={filterName} onClick={() => handleFilterClick(filterName)} className={styles.filterItemStyle}>
                      {filterName}
                    </MenuItem>
                  );
                })}
              </MenuList>
            ) : (
              <div className={styles.selectedFilterOptionMenuStyle} ref={subOptionsListRef} tabIndex={-1}>
                <div className={styles.selectedFilterOptionHeaderStyle}>
                  <Button
                    icon={<ArrowLeftRegular style={{ marginRight: '5px' }} />}
                    appearance="subtle"
                    size="small"
                    onClick={() => setSubOptionsVisible(false)}
                    className={styles.selectedFilterButtonStyle}
                  >
                    {currentFilter}
                  </Button>
                  <Button appearance="subtle" size="small" onClick={clearAll} className={styles.clearAllButtonStyle}>
                    Clear all
                  </Button>
                </div>
                <div className={styles.searchAndToggleContainerStyle}>
                  <Input
                    placeholder="Search name or ID"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    contentBefore={<SearchRegular />}
                    className={styles.searchBarStyle}
                  />
                  <div className={styles.toggleButtonStyle}>
                    <Switch
                      label="Show selected"
                      checked={showSelected}
                      onChange={(e, data) => setShowSelected(data.checked)}
                      className={styles.switchButtonStyle}
                    />
                  </div>
                </div>
                <div className={styles.filterOptionsContainerStyle}>
                  <Tree aria-label="Nested Filters">{renderTreeItems(filteredOptions as IPubCenterMetadataElement[], currentFilter)}</Tree>
                </div>
                <div className={styles.applyAndCancelContainerStyle}>
                  <Button appearance="primary" onClick={applyFilter} className={styles.primaryButton}>
                    Apply
                  </Button>
                  <Button appearance="secondary" onClick={cancelFilter} className={styles.secondaryButton}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </PopoverSurface>
        </Popover>
      </InteractionTag>
    );
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <div className={styles.filterComponentContainerStyle}>
        {/* Filter Button */}
        {FiltersOptions()}
        {/* Selected Filters with Clear Buttons */}
        <TagGroup onDismiss={removeItem}>
          {Object.entries(existingFilters).map(([filterKey, items]) => (
            <InteractionTag
              shape="circular"
              appearance="brand"
              key={filterKey}
              value={filterKeyToDisplayName[filterKey]}
              className={styles.selectedFilterContainerStyle}
            >
              <InteractionTagPrimary
                hasSecondaryAction
                className={styles.selectedFilterStyle}
              >{`${filterKeyToDisplayName[filterKey]} = ${items.length}`}</InteractionTagPrimary>
              <InteractionTagSecondary aria-label="remove" className={styles.selectedFilterRemoveButtonStyle} />
            </InteractionTag>
          ))}
        </TagGroup>
      </div>
    </FluentProvider>
  );
};
